import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Clock, MapPin, Globe, ChevronLeft, ChevronRight, Check, Mail, Phone, ExternalLink, Calendar } from 'lucide-react';
import axios from 'axios';
import { PhoneInput, DEFAULT_PHONE, formatPhoneString, parsePhoneString, PhoneValue } from '@/components/ui/PhoneInput';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { formatDate } from '@/lib/dateFormat';

// Public booking pages never expose the counselor's personal name — show a
// neutral role label instead (org branding stays via tenant.name).
const ADVISOR_LABEL = 'Your Advisor';

// ─── API ──────────────────────────────────────────────────────────────────────

const _env = import.meta.env;
const API_BASE = _env.VITE_API_URL
  ? `${_env.VITE_API_URL}/api`
  : (_env.DEV ? 'http://localhost:3000/api' : '/api');

const pub = axios.create({ baseURL: API_BASE });

// ─── Types ────────────────────────────────────────────────────────────────────

interface BookingPageSettings {
  welcomeMessage?: string;
  thankYouMessage?: string;
  showPhone?: boolean;
  showEmail?: boolean;
  showWebsite?: boolean;
  footerNote?: string;
}

interface TenantInfo {
  name: string;
  logo: string | null;
  phones: string[];
  emails: string[];
  websiteUrl: string;
  bookingPageSettings: BookingPageSettings;
}

interface PubEvent {
  id: string;
  slug: string;
  name: string;
  duration: number;
  description?: string;
  location?: string;
  meetingLink?: string;
  color: string;
  user: { id: string; name: string };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function fmt12(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${String(h % 12 || 12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
}

function toDateStr(y: number, mo: number, d: number) {
  return `${y}-${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function fmtDate(ds: string) {
  const d = new Date(ds + 'T00:00:00');
  const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  return `${DAYS[d.getDay()]}, ${formatDate(d)}`;
}

function orgInitials(name: string): string {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

// ─── Timezone conversion ─────────────────────────────────────────────────────
//
// Slots come from the API as HH:MM wall-clock in the counselor's TZ.
// We convert them to the guest's chosen TZ for display, then send the
// UNCHANGED counselor-TZ date+time back to the API on submit — the
// server already indexes bookings by counselor date+time and just
// stores the guest TZ label alongside for the notification email.

/** True UTC epoch-ms for a wall-clock date+time in a given IANA tz. */
function wallClockToUtcMs(dateStr: string, hhmm: string, tz: string): number {
  const [Y, M, D] = dateStr.split('-').map(Number);
  const [h, m] = hhmm.split(':').map(Number);
  const guess = Date.UTC(Y, (M ?? 1) - 1, D ?? 1, h ?? 0, m ?? 0);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts: Record<string, string> = {};
  for (const p of dtf.formatToParts(new Date(guess))) if (p.type !== 'literal') parts[p.type] = p.value;
  const hh = parts.hour === '24' ? '00' : parts.hour;
  const wallInTz = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(hh), Number(parts.minute), Number(parts.second),
  );
  return guess - (wallInTz - guess);
}

/** Convert a wall-clock (date, hhmm) from one IANA tz to another. */
function convertSlot(dateStr: string, hhmm: string, fromTz: string, toTz: string): { date: string; time: string } {
  const utcMs = wallClockToUtcMs(dateStr, hhmm, fromTz);
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: toTz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
  const parts: Record<string, string> = {};
  for (const p of dtf.formatToParts(new Date(utcMs))) if (p.type !== 'literal') parts[p.type] = p.value;
  const hh = parts.hour === '24' ? '00' : parts.hour;
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${hh}:${parts.minute}`,
  };
}

/** YYYY-MM-DD for a UTC epoch-ms as observed in the given IANA tz. */
function utcMsToTzDate(utcMs: number, tz: string): string {
  try {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const parts: Record<string, string> = {};
    for (const p of dtf.formatToParts(new Date(utcMs))) if (p.type !== 'literal') parts[p.type] = p.value;
    return `${parts.year}-${parts.month}-${parts.day}`;
  } catch {
    const d = new Date(utcMs);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  }
}

/** Full IANA tz list from the browser, falling back to a curated set. */
function getAllTimezones(): string[] {
  const anyIntl = Intl as any;
  if (typeof anyIntl.supportedValuesOf === 'function') {
    try { return anyIntl.supportedValuesOf('timeZone'); } catch { /* noop */ }
  }
  return [
    'UTC',
    'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Australia/Sydney', 'Pacific/Auckland',
  ];
}

// ─── ICS generator ────────────────────────────────────────────────────────────

function generateIcs(params: {
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;
  location?: string;
  meetingLink?: string;
  description?: string;
  organizerName: string;
  attendeeEmail: string;
}): string {
  const dt = (dateStr: string, timeStr: string) =>
    `${dateStr.replace(/-/g, '')}T${timeStr.replace(':', '').replace(':', '')}00`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BrixLead//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:REQUEST',
    'BEGIN:VEVENT',
    `DTSTART;TZID=${params.timezone}:${dt(params.date, params.startTime)}`,
    `DTEND;TZID=${params.timezone}:${dt(params.date, params.endTime)}`,
    `SUMMARY:${params.title}`,
    params.location ? `LOCATION:${params.location}` : '',
    params.meetingLink ? `URL:${params.meetingLink}` : '',
    params.description ? `DESCRIPTION:${params.description}` : '',
    `ORGANIZER;CN=${params.organizerName}:mailto:noreply@brixlead.com`,
    `ATTENDEE;CN=Guest:mailto:${params.attendeeEmail}`,
    `UID:${Date.now()}@autoleadcrm`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  return lines.join('\r\n');
}

function downloadIcs(ics: string, filename: string) {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Calendar picker ──────────────────────────────────────────────────────────

function CalPicker({ color, selected, onSelect }: {
  color: string;
  selected: string | null;
  onSelect: (d: string) => void;
}) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const first = new Date(year, month, 1).getDay();
  const dim = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);

  const isPast = (d: number) => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    return new Date(year, month, d) < t;
  };

  const atMin = year === today.getFullYear() && month === today.getMonth();
  const prev = () => { if (atMin) return; month === 0 ? (setMonth(11), setYear(y => y - 1)) : setMonth(m => m - 1); };
  const next = () => month === 11 ? (setMonth(0), setYear(y => y + 1)) : setMonth(m => m + 1);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <button onClick={prev} disabled={atMin} className="h-9 w-9 flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] disabled:opacity-30 disabled:hover:border-slate-200 disabled:hover:text-slate-600 transition-colors shadow-sm">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="font-bold text-slate-900 text-base tracking-tight">{MONTHS[month]} {year}</span>
        <button onClick={next} className="h-9 w-9 flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] transition-colors shadow-sm">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map(d => <div key={d} className="text-center text-xs font-semibold text-slate-500 py-2 uppercase tracking-wider">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds = toDateStr(year, month, d);
          const past = isPast(d);
          const sel = selected === ds;
          return (
            <button key={i} onClick={() => !past && onSelect(ds)} disabled={past}
              className={`h-10 w-10 mx-auto rounded-full text-sm font-medium transition-all ${past ? 'text-slate-300 cursor-not-allowed' : sel ? 'text-white shadow-md' : 'text-slate-800 cursor-pointer hover:bg-indigo-50 hover:text-[#6366F1]'}`}
              style={sel ? { backgroundColor: color, color: '#fff' } : undefined}>
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Left info panel ──────────────────────────────────────────────────────────

function InfoPanel({
  event, tenant, guestTz, counselorTz, onGuestTzChange,
}: {
  event: PubEvent;
  tenant: TenantInfo | null;
  guestTz: string;
  counselorTz: string;
  onGuestTzChange?: (tz: string) => void;
}) {
  const bps = tenant?.bookingPageSettings ?? {};
  const welcome = bps.welcomeMessage || 'Book a time that works for you';
  const tzList = getAllTimezones();

  return (
    <div className="lg:w-80 flex-shrink-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 border-b lg:border-b-0 lg:border-r border-slate-100 p-7 space-y-5">
      {/* Counselor avatar + name */}
      <div className="flex flex-col items-start gap-3">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0 shadow-md ring-4 ring-white"
          style={{ backgroundColor: event.color }}
        >
          A
        </div>
        <div>
          <p className="font-bold text-slate-900 text-lg leading-tight">{ADVISOR_LABEL}</p>
          {tenant && <p className="text-sm text-slate-500 mt-0.5">{tenant.name}</p>}
        </div>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed">{welcome}</p>

      <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200/60 to-transparent" />

      {/* Event details */}
      <div className="space-y-2.5">
        <p className="font-semibold text-slate-900 text-sm">{event.name}</p>
        <div className="space-y-2 text-sm text-slate-600">
          <span className="flex items-center gap-2.5">
            <span className="h-7 w-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm">
              <Clock className="h-3.5 w-3.5 text-slate-500" />
            </span>
            {event.duration} min
          </span>
          {event.location && (
            <span className="flex items-center gap-2.5">
              <span className="h-7 w-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                <MapPin className="h-3.5 w-3.5 text-slate-500" />
              </span>
              {event.location}
            </span>
          )}
          {/* Guest TZ — picker so international guests can see slots in
              their own clock. Defaults to the browser TZ. */}
          <div className="flex items-start gap-2.5">
            <span className="h-7 w-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm flex-shrink-0 mt-0.5">
              <Globe className="h-3.5 w-3.5 text-slate-500" />
            </span>
            <div className="flex-1 min-w-0">
              {onGuestTzChange ? (
                <SearchableSelect
                  options={tzList}
                  value={guestTz}
                  onChange={onGuestTzChange}
                  placeholder="Select your timezone"
                />
              ) : (
                <span>{guestTz}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {event.description && (
        <>
          <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200/60 to-transparent" />
          <p className="text-sm text-slate-600 leading-relaxed">{event.description}</p>
        </>
      )}

      {/* Contact info */}
      {tenant && (tenant.phones.length > 0 || tenant.emails.length > 0 || tenant.websiteUrl) && (
        <>
          <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200/60 to-transparent" />
          <div className="space-y-2.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</p>
            {tenant.emails[0] && (
              <a href={`mailto:${tenant.emails[0]}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#6366F1] transition-colors">
                <Mail className="h-3.5 w-3.5 flex-shrink-0" />{tenant.emails[0]}
              </a>
            )}
            {tenant.phones[0] && (
              <a href={`tel:${tenant.phones[0]}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#6366F1] transition-colors">
                <Phone className="h-3.5 w-3.5 flex-shrink-0" />{tenant.phones[0]}
              </a>
            )}
            {tenant.websiteUrl && (
              <a href={tenant.websiteUrl.startsWith('http') ? tenant.websiteUrl : `https://${tenant.websiteUrl}`}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#6366F1] transition-colors">
                <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />{tenant.websiteUrl.replace(/^https?:\/\//, '')}
              </a>
            )}
          </div>
        </>
      )}

      {bps.footerNote && (
        <>
          <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200/60 to-transparent" />
          <p className="text-xs text-slate-500 italic leading-relaxed">{bps.footerNote}</p>
        </>
      )}
    </div>
  );
}

// ─── Page header ──────────────────────────────────────────────────────────────

function PageHeader({ tenant }: { tenant: TenantInfo | null }) {
  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/70 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
      <div className="flex items-center gap-3">
        {tenant?.logo ? (
          <img src={tenant.logo} alt={tenant.name} className="h-10 rounded-lg object-contain" />
        ) : (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
            {tenant ? orgInitials(tenant.name) : 'AL'}
          </div>
        )}
        <span className="font-bold text-slate-900 text-base tracking-tight">{tenant?.name || 'Booking'}</span>
      </div>
      <a
        href="https://brixlead.com"
        target="_blank"
        rel="noreferrer"
        className="text-xs text-slate-500 hover:text-[#6366F1] transition-colors flex items-center gap-1.5"
      >
        Powered by <span className="font-bold bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] bg-clip-text text-transparent">BrixLead</span>
      </a>
    </header>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

type Step = 'pick' | 'form' | 'done';

export function BookingPage() {
  // Two ways in: /book/:userId/:eventSlug, or a lead's short link /m/:code
  // (resolved to the counselor + event below). Lead links prefill the guest
  // details and attach the booking to that lead; older long links carry
  // a signed ?ref=… instead.
  const params = useParams<{ userId?: string; eventSlug?: string; code?: string }>();
  const linkCode = params.code;
  const [linkTarget, setLinkTarget] = useState<{ userId: string; eventSlug: string } | null>(null);
  const userId = params.userId ?? linkTarget?.userId;
  const eventSlug = params.eventSlug ?? linkTarget?.eventSlug;
  const [searchParams] = useSearchParams();
  const leadRef = searchParams.get('ref') || undefined;
  const [event, setEvent] = useState<PubEvent | null>(null);
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [date, setDate] = useState<string | null>(null);
  // Slot rows carry BOTH the counselor's wall-clock (what the server
  // uses to index availability + must be sent back on submit) and the
  // guest's wall-clock (what we render). The picked date is a guest
  // date, so we fetch adjacent counselor dates and keep only slots
  // that land on the selected guest date.
  type SlotRow = { counselorDate: string; counselorTime: string; guestTime: string };
  const [slots, setSlots] = useState<SlotRow[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState<SlotRow | null>(null);
  const [step, setStep] = useState<Step>('pick');

  // Phone as a { countryCode, number } pair so guests pick their dial
  // code (same UX as the New Lead form). Serialised to a normal string
  // with formatPhoneString before POSTing.
  const [form, setForm] = useState<{
    name: string;
    email: string;
    phone: PhoneValue;
    notes: string;
  }>({ name: '', email: '', phone: { ...DEFAULT_PHONE }, notes: '' });
  const [prefill, setPrefill] = useState<{ name: string; email: string; phone: PhoneValue }>({
    name: '', email: '', phone: { ...DEFAULT_PHONE },
  });

  const applyPrefill = (data: { name?: string | null; email?: string | null; phone?: string | null } | null) => {
    const p = {
      name: data?.name || '',
      email: data?.email || '',
      phone: data?.phone ? parsePhoneString(data.phone) : { ...DEFAULT_PHONE },
    };
    setPrefill(p);
    // Fill only what the guest hasn't typed yet; fields stay editable.
    setForm(f => ({
      ...f,
      name: f.name || p.name,
      email: f.email || p.email,
      phone: f.phone.number ? f.phone : p.phone,
    }));
  };

  // Short link → counselor + event + guest details.
  useEffect(() => {
    if (!linkCode) return;
    pub.get(`/public/booking-links/${encodeURIComponent(linkCode)}`)
      .then(({ data }) => {
        setLinkTarget({ userId: data.userId, eventSlug: data.eventSlug });
        applyPrefill(data.prefill);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkCode]);

  useEffect(() => {
    if (linkCode || !leadRef || !userId || !eventSlug) return;
    pub.get(`/book/${userId}/${eventSlug}/prefill`, { params: { ref: leadRef } })
      .then(({ data }) => applyPrefill(data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkCode, leadRef, userId, eventSlug]);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState<any>(null);

  // Counselor's TZ comes from the API (their Availability record). We
  // hold this + the guest's chosen TZ as separate state so we can
  // convert slot times between them.
  const [counselorTz, setCounselorTz] = useState<string>('Asia/Kolkata');
  const [guestTz, setGuestTz] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );

  useEffect(() => {
    if (!userId || !eventSlug) return;
    pub.get(`/book/${userId}`)
      .then(r => {
        const data = r.data ?? {};
        const evts: PubEvent[] = data.events ?? (Array.isArray(data) ? data : []);
        const found = evts.find(e => e.id === eventSlug || e.slug === eventSlug);
        if (found) {
          setEvent(found);
          setTenant(data.tenant ?? null);
          if (data.counselorTimezone) setCounselorTz(data.counselorTimezone);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [userId, eventSlug]);

  useEffect(() => {
    if (!date || !userId || !eventSlug) return;
    setSlotsLoading(true);
    setSlot(null);

    // The picked `date` is a GUEST-TZ date. Figure out which
    // counselor-TZ date(s) can produce slots that land on that guest
    // date. Convert the guest day's start + end instants to counselor
    // TZ dates — usually the same date, but large offsets (e.g.
    // America/Mexico_City ↔ Asia/Kolkata is ~11.5h) span two
    // consecutive counselor dates.
    const startUtc = wallClockToUtcMs(date, '00:00', guestTz);
    const endUtc = wallClockToUtcMs(date, '23:59', guestTz);
    const dStart = utcMsToTzDate(startUtc, counselorTz);
    const dEnd = utcMsToTzDate(endUtc, counselorTz);
    const counselorDates = dStart === dEnd ? [dStart] : [dStart, dEnd];

    Promise.all(
      counselorDates.map((cd) =>
        pub.get(`/book/${userId}/${eventSlug}/slots?date=${cd}`)
          .then((r) => (Array.isArray(r.data) ? r.data as string[] : []).map((t) => ({ counselorDate: cd, counselorTime: t })))
          .catch(() => [] as { counselorDate: string; counselorTime: string }[]),
      ),
    )
      .then((all) => {
        const flat = all.flat();
        // Convert each counselor slot to guest TZ, drop any that
        // don't land on the selected guest date, then sort chrono.
        const kept: SlotRow[] = flat
          .map((s) => {
            const conv = convertSlot(s.counselorDate, s.counselorTime, counselorTz, guestTz);
            if (conv.date !== date) return null;
            return { counselorDate: s.counselorDate, counselorTime: s.counselorTime, guestTime: conv.time };
          })
          .filter((v): v is SlotRow => v !== null)
          .sort((a, b) => a.guestTime.localeCompare(b.guestTime));
        setSlots(kept);
      })
      .finally(() => setSlotsLoading(false));
  }, [date, userId, eventSlug, guestTz, counselorTz]);

  const submit = async () => {
    if (!form.name || !form.email || !date || !slot) return;
    setSubmitting(true);
    try {
      // Compose the phone into the "+91 9876543210" shape the backend
      // already handles. Skip sending when the number field is empty so
      // guests can leave it blank without triggering validation on the
      // dial-code-only string.
      const phoneStr = form.phone.number.trim()
        ? formatPhoneString(form.phone)
        : undefined;
      // date + startTime stay in counselor TZ (that's how the server
      // indexes availability). timezone = guest TZ so the confirmation
      // email + calendar file render times in the guest's clock.
      //
      // browser: silent fingerprint sent alongside — server stores this
      // in guestMeta and enriches with IP-based geo (ip-api.com). Not
      // GPS-accurate; browser Geolocation API would require a permission
      // prompt (out of scope for the "auto behind the code" contract).
      const browserInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        languages: navigator.languages,
        platform: (navigator as any).platform ?? null,
        vendor: (navigator as any).vendor ?? null,
        screen: {
          width: window.screen?.width ?? null,
          height: window.screen?.height ?? null,
          pixelRatio: window.devicePixelRatio ?? null,
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        timezone: guestTz,
        timezoneOffset: new Date().getTimezoneOffset(),
        referrer: document.referrer || null,
        pageUrl: window.location.href,
      };
      // Send the COUNSELOR-TZ date + startTime — that's how the
      // server indexes availability, and it's what the counselor
      // sees on their own calendar. `timezone` = guest TZ so the
      // confirmation email + ICS render in the guest's clock.
      const res = await pub.post(`/book/${userId}/${eventSlug}`, {
        date: slot.counselorDate,
        startTime: slot.counselorTime,
        timezone: guestTz,
        guestName: form.name, guestEmail: form.email,
        guestPhone: phoneStr,
        notes: form.notes || undefined,
        ref: linkCode ? undefined : leadRef,
        link: linkCode,
        browser: browserInfo,
      });
      setBooking(res.data);
      setStep('done');
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="animate-spin h-10 w-10 border-4 border-[#6366F1] border-t-transparent rounded-full" />
    </div>
  );

  if (notFound || !event) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 text-center px-4">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-10 max-w-md">
        <div className="w-14 h-14 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">🔗</span>
        </div>
        <p className="text-2xl font-bold text-slate-900 mb-2">Booking link not found</p>
        <p className="text-slate-500">This booking link may be inactive or invalid. Please check the URL or contact the organizer.</p>
      </div>
    </div>
  );

  // ── Confirmation screen ──────────────────────────────────────────────────────
  if (step === 'done' && booking) {
    const thankYou = tenant?.bookingPageSettings?.thankYouMessage || 'Thanks for booking! See you soon.';
    // booking.date + booking.startTime are stored in counselor's TZ
    // (that's how the server indexes availability). Convert to guest
    // TZ for display + ICS so what the guest sees on the confirmation
    // matches what lands on their calendar app.
    const guestConv = convertSlot(booking.date, booking.startTime, counselorTz, guestTz);
    const guestEndTime = addMinutesToTime(guestConv.time, event.duration);

    const handleAddToCalendar = () => {
      const ics = generateIcs({
        title: `${event.name} with ${ADVISOR_LABEL}`,
        date: guestConv.date,
        startTime: guestConv.time,
        endTime: guestEndTime,
        timezone: guestTz,
        location: event.location,
        meetingLink: booking.meetingLink,
        description: event.description,
        organizerName: ADVISOR_LABEL,
        attendeeEmail: form.email,
      });
      downloadIcs(ics, `booking-${booking.id}.ics`);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex flex-col">
        <PageHeader tenant={tenant} />
        <div className="flex-1 flex items-stretch py-6 sm:py-10 px-4">
          <div className="max-w-5xl w-full mx-auto bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col lg:flex-row">
            <InfoPanel event={event} tenant={tenant} guestTz={guestTz} counselorTz={counselorTz} />

            <div className="flex-1 p-8 sm:p-10 flex items-center justify-center">
              <div className="max-w-md w-full">
                {/* Big checkmark with animated ring */}
                <div className="text-center mb-7">
                  <div className="relative w-24 h-24 mx-auto mb-5">
                    <div className="absolute inset-0 rounded-full bg-emerald-100 animate-ping opacity-30" />
                    <div className="relative w-24 h-24 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-200">
                      <Check className="h-12 w-12 text-white" strokeWidth={3} />
                    </div>
                  </div>
                  <h1 className="text-3xl font-extrabold text-slate-900 mb-2 tracking-tight">Booking Confirmed!</h1>
                  <p className="text-slate-600">{thankYou}</p>
                </div>

                {/* Details card */}
                <div className="bg-gradient-to-br from-slate-50 to-indigo-50/40 rounded-2xl border border-slate-200 p-5 space-y-3 mb-5">
                  <div className="flex items-center gap-3 text-sm text-slate-800">
                    <span className="h-8 w-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm flex-shrink-0">
                      <Calendar className="h-4 w-4 text-[#6366F1]" />
                    </span>
                    <span className="font-semibold">{fmtDate(guestConv.date)} at {fmt12(guestConv.time)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <span className="h-8 w-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm flex-shrink-0">
                      <Clock className="h-4 w-4 text-slate-500" />
                    </span>
                    <span>{event.duration} minutes</span>
                  </div>
                  {(event.location || booking.meetingLink) && (
                    <div className="flex items-center gap-3 text-sm text-slate-700">
                      <span className="h-8 w-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm flex-shrink-0">
                        <MapPin className="h-4 w-4 text-slate-500" />
                      </span>
                      <span>{event.location || 'Online'}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <span className="h-8 w-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm flex-shrink-0">
                      <Globe className="h-4 w-4 text-slate-500" />
                    </span>
                    <span>{guestTz}</span>
                  </div>
                </div>

                {/* The join link isn't useful until the slot — it goes out by
                    email (confirmation now + a reminder before the meeting). */}
                <div className="flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 mb-5 text-sm text-slate-700">
                  <Mail className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#6366F1]" />
                  <p>
                    {booking.meetingLink ? 'The meeting link has been sent' : 'The meeting details have been sent'}
                    {form.email ? <> to <span className="font-semibold">{form.email}</span></> : ' to you'}.
                    {' '}You'll also get a reminder before the meeting starts.
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleAddToCalendar}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:border-[#6366F1] hover:text-[#6366F1] hover:bg-indigo-50/40 transition-all shadow-sm"
                  >
                    <Calendar className="h-4 w-4" /> Add to Calendar
                  </button>
                  <button
                    onClick={() => { setStep('pick'); setDate(null); setSlot(null); setForm({ ...prefill, phone: { ...prefill.phone }, notes: '' }); setBooking(null); }}
                    className="text-sm text-slate-500 hover:text-[#6366F1] transition-colors text-center font-medium"
                  >
                    Book another time
                  </button>
                </div>

                <p className="text-xs text-slate-400 mt-5 text-center font-mono">Booking ID: {booking.id}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Booking flow ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex flex-col">
      <PageHeader tenant={tenant} />

      <div className="flex-1 py-6 sm:py-10 px-4">
        <div className="max-w-5xl w-full mx-auto bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col lg:flex-row">
          <InfoPanel event={event} tenant={tenant} guestTz={guestTz} counselorTz={counselorTz} onGuestTzChange={setGuestTz} />

          {/* Right column */}
          <div className="flex-1 p-7 sm:p-8">
          {/* Event title bar */}
          <div className="flex items-center gap-3 mb-7 pb-5 border-b border-slate-100">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
              style={{ backgroundColor: event.color + '15', boxShadow: `0 0 0 1px ${event.color}30` }}
            >
              <Clock className="h-5 w-5" style={{ color: event.color }} />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 text-base">{event.name}</h2>
              <p className="text-sm text-slate-500">{event.duration} min · {ADVISOR_LABEL}</p>
            </div>
          </div>

          {/* Step: pick date + slot */}
          {step === 'pick' && (
            <div className="lg:flex gap-8">
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900 mb-4 uppercase tracking-wider text-xs">Select a date</p>
                <CalPicker color={event.color} selected={date} onSelect={setDate} />
              </div>

              {date && (
                <div className="lg:w-60 mt-7 lg:mt-0">
                  <p className="text-sm font-bold text-slate-900">{fmtDate(date)}</p>
                  <p className="text-xs text-slate-500 mb-4 mt-0.5">Times in {guestTz}</p>
                  {slotsLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin h-6 w-6 border-4 border-[#6366F1] border-t-transparent rounded-full" />
                    </div>
                  ) : slots.length === 0 ? (
                    <div className="text-center py-6 px-4 rounded-xl bg-slate-50 border border-dashed border-slate-200">
                      <p className="text-sm text-slate-500">No available slots for this date.</p>
                      <p className="text-xs text-slate-400 mt-1">Try another date.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {slots.map((s) => (
                        <button
                          key={`${s.counselorDate}T${s.counselorTime}`}
                          onClick={() => { setSlot(s); setStep('form'); }}
                          className="w-full py-3 text-sm font-semibold rounded-xl border-2 transition-all hover:-translate-y-0.5 hover:shadow-md"
                          style={{ borderColor: event.color, color: event.color }}
                          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = event.color; (e.currentTarget as HTMLButtonElement).style.color = '#fff'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = ''; (e.currentTarget as HTMLButtonElement).style.color = event.color; }}
                        >
                          {fmt12(s.guestTime)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step: booking form */}
          {step === 'form' && (
            <div className="max-w-lg">
              <button onClick={() => setStep('pick')} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#6366F1] mb-6 font-medium transition-colors">
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-5 mb-6 border border-indigo-100">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#6366F1] mb-1.5">Your booking</p>
                {slot && (
                  <>
                    <p className="text-base font-bold text-slate-900">{fmtDate(date!)} at {fmt12(slot.guestTime)}</p>
                    <p className="text-xs text-slate-600 mt-1">{event.duration} min · {guestTz}</p>
                  </>
                )}
              </div>
              <div className="space-y-4">
                <Field label="Your Name *" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="Full name" />
                <Field label="Email *" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} placeholder="you@email.com" />
                {/* Phone with a searchable dial-code selector — same
                    component + defaults as the New Lead form. Keeps the
                    stored value shape consistent with lead.phones so the
                    booking → auto-lead-link flow doesn't have to reparse
                    a free-form string. */}
                <div>
                  <label className="text-sm font-normal text-slate-700 mb-1.5 block">Phone</label>
                  <PhoneInput
                    value={form.phone}
                    onChange={v => setForm(f => ({ ...f, phone: v }))}
                    placeholder="9876543210"
                  />
                </div>
                <div>
                  <label className="text-sm font-normal text-slate-700 mb-1.5 block">Notes</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3}
                    placeholder="Anything you'd like to share…"
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40 focus:border-[#6366F1] resize-none transition-all" />
                </div>
                <button
                  onClick={submit}
                  disabled={submitting || !form.name || !form.email}
                  className="w-full py-3.5 text-sm font-semibold text-white rounded-xl disabled:opacity-50 shadow-lg shadow-indigo-200/50 hover:shadow-xl transition-all hover:-translate-y-0.5 active:translate-y-0"
                  style={{ backgroundColor: event.color }}
                >
                  {submitting ? 'Booking…' : 'Confirm Booking'}
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Small helpers ─────────────────────────────────────────────────────────────

function Field({ label, value, onChange, placeholder, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string;
}) {
  return (
    <div>
      <label className="text-sm font-normal text-slate-700 mb-1.5 block">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40 focus:border-[#6366F1] transition-all" />
    </div>
  );
}
